import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: ('admin' | 'editor')[];
}

const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-bg-page flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-6">Loading...</p>
    </div>
  </div>
);

const UnauthorizedAccess: React.FC = () => (
  <div className="min-h-screen bg-bg-page flex items-center justify-center">
    <div className="admin-card max-w-md mx-4 text-center">
      <div className="text-error text-6xl mb-4">🚫</div>
      <h1 className="text-xl font-bold text-gray-9 mb-2">
        Access Denied
      </h1>
      <p className="text-gray-6 mb-4">
        You don't have permission to access this resource.
      </p>
      <button
        onClick={() => window.history.back()}
        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  
  console.log('ProtectedRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'user:', user);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (roles && roles.length > 0) {
    if (!user || !roles.includes(user.role)) {
      return <UnauthorizedAccess />;
    }
  }

  // Check if user is active
  if (user && !user.isActive) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center">
        <div className="admin-card max-w-md mx-4 text-center">
          <div className="text-warning text-6xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-9 mb-2">
            Account Inactive
          </h1>
          <p className="text-gray-6 mb-4">
            Your account has been deactivated. Please contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;